import {
  asyncScheduler,
  catchError,
  concatMap,
  EMPTY,
  filter,
  from,
  ignoreElements,
  Observable,
  scheduled,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { history } from '../navigation/history';
import { ofType } from 'redux-observable';
import { providerSlice } from '../provider';
import { authSlice } from '../auth';
import { mixpanelSlice } from '../mixpanel';
import { selectedDateSlice } from '../selectedDate';
import { selectedVisitSlice } from '../selectedVisit';
import { selectMixpanelToken } from '../mixpanel/selectors';
import { UserSignedInPayloadInterface } from '../auth/interfaces';
import { RootState } from '@ax/redux-store';
import { LoginStage, routerPaths } from '@ax/constants';
import {
  selectHasPasscodeEnabled,
  selectIsPinEnabled,
  selectIsProviderAdmin,
  selectProviderUserInfo,
} from '../provider/selectors';
import {
  AnalyticsService,
  createPasscodeSession,
  hasForcePasscodeReset,
  logout,
} from '@ax/service';
import * as Sentry from '@sentry/react';
import { PayloadAction } from '@reduxjs/toolkit';
import { Auth } from '@ax/auth';

export const completeLoginAfterProviderStateLoaded = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(providerSlice.actions.initializeProviderStateSucceeded.type),
    withLatestFrom(state$),
    filter(() => [routerPaths.login, routerPaths.ssoSuccess].includes(history.location.pathname)),
    concatMap(([_, state]) => {
      const isPINEnabled = selectIsPinEnabled(state);
      const userInfo = selectProviderUserInfo(state);
      const isProviderAdmin = selectIsProviderAdmin(state);
      const redirectPath = isProviderAdmin ? routerPaths.adminNotes : routerPaths.notes;

      if (!isPINEnabled) {
        history.push(redirectPath);
        return scheduled([authSlice.actions.signInCompleted()], asyncScheduler);
      }

      const hasPasscodeEnabled = selectHasPasscodeEnabled(state);

      if (hasPasscodeEnabled && !hasForcePasscodeReset()) {
        createPasscodeSession(userInfo?.name ?? '', userInfo.email ?? '');
        history.push(redirectPath);
        return scheduled([authSlice.actions.signInCompleted()], asyncScheduler);
      }

      return scheduled(
        [
          authSlice.actions.loginStageChanged({ stage: LoginStage.CreatePasscode }),
          authSlice.actions.signInCompleted(),
        ],
        asyncScheduler,
      );
    }),
  );

export const redirectToLoginAfterFailedPasscodeLogin = (action$: Observable<any>) =>
  action$.pipe(
    ofType(authSlice.actions.passcodeLoginAttemptsExceeded.type),
    concatMap(() => {
      history.push(routerPaths.login);
      return scheduled(
        [authSlice.actions.loginStageChanged({ stage: LoginStage.Identity })],
        asyncScheduler,
      );
    }),
  );

export const setUserInfoOnSentry = (action$: Observable<any>) =>
  action$.pipe(
    ofType(authSlice.actions.userSignedIn.type),
    tap((action: PayloadAction<UserSignedInPayloadInterface>) => {
      Sentry.setUser({
        email: action.payload.username,
      });
    }),
    ignoreElements(),
  );

export const unsetUserInfoOnSentry = (action$: Observable<any>) =>
  action$.pipe(
    ofType(authSlice.actions.userSignedOut.type),
    tap(() => {
      Sentry.setUser(null);
    }),
    ignoreElements(),
  );

export const onLogoutRequested = (action$: Observable<any>, state$: Observable<RootState>) =>
  action$.pipe(
    ofType(authSlice.actions.logoutRequested.type),
    withLatestFrom(state$),
    concatMap(([_, state]) => {
      return from(logout({ logoutType: 'General' })).pipe(
        switchMap((_) => {
          const mixpanelToken = selectMixpanelToken(state);
          const providerUserInfo = selectProviderUserInfo(state);
          const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

          const dispatchedEvent = analyticsService.onUserLogout();
          Auth.logout();

          if (!Auth.isLoggedIn()) {
            history.push(routerPaths.root);

            return scheduled(
              [
                authSlice.actions.resetPasscodeSession(),
                selectedDateSlice.actions.setSelectedDate({ date: '' }),
                selectedVisitSlice.actions.clearVisitDetails(),
                mixpanelSlice.actions.eventDispatchSucceeded(dispatchedEvent),
                authSlice.actions.logoutSucceeded(),
                authSlice.actions.userSignedOut(),
              ],
              asyncScheduler,
            );
          }

          return EMPTY;
        }),
        catchError((error) => {
          return [authSlice.actions.logoutFailed({ error })];
        }),
      );
    }),
  );

export default [
  completeLoginAfterProviderStateLoaded,
  redirectToLoginAfterFailedPasscodeLogin,
  setUserInfoOnSentry,
  unsetUserInfoOnSentry,
  onLogoutRequested,
];
